// cSpell:ignore xyflow Silae SILAE
import { Handle } from "@xyflow/react";
import { type NodeProps, type Node } from "@xyflow/react";
import { FileCode2 } from "lucide-react";

import type { GraphNodeData } from "../utils/buildGraph";
import { useDiagramHandlePositions } from "../hooks/useDiagramHandlePositions";
import { useTheme } from "@/hooks/useTheme";
import { NODE_DEFAULT_WIDTH } from "../utils/diagramConfig";

const GLOW_LIGHT =
  "0 0 0 1px rgba(109,40,217,0.15), 0 4px 24px -4px rgba(109,40,217,0.18)";
const GLOW_DARK =
  "0 0 0 1px rgba(167,139,250,0.2), 0 4px 32px -4px rgba(167,139,250,0.32)";

export function SourceNode({ data }: NodeProps<Node<GraphNodeData>>) {
  const { source } = useDiagramHandlePositions();
  const { isDark } = useTheme();
  const meta = data.codeMeta;

  return (
    <div
      style={{
        width: NODE_DEFAULT_WIDTH,
        boxShadow: isDark ? GLOW_DARK : GLOW_LIGHT,
      }}
      className="relative rounded-lg border-2 border-violet-600 dark:border-violet-500 bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50"
    >
      {/* Liseré d'accent — même pattern que NodeShell */}
      <div
        className="h-1 rounded-t-3xl bg-violet-700 dark:bg-violet-400"
        aria-hidden="true"
      />

      {/* Header — même structure que NodeShell */}
      <header className="flex items-center gap-3 min-w-0 px-3 py-2 bg-violet-600 dark:bg-violet-500 text-white">
        <FileCode2
          className="shrink-0 text-white"
          size={32}
          aria-hidden="true"
        />
        <h3 className="min-w-0 flex-1 text-[18px] font-semibold tracking-tight leading-snug break-words line-clamp-2 text-left">
          {meta?.name ?? "Code SILAE"}
        </h3>
      </header>

      {/* Corps */}
      {(meta?.description || (meta?.tags && meta.tags.length > 0)) && (
        <div className="px-3 py-3 rounded-b-lg space-y-2.5">
          {meta?.description && (
            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed break-words whitespace-pre-wrap">
              {meta.description}
            </p>
          )}
          {meta?.tags && meta.tags.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[10px] font-semibold text-violet-500 dark:text-violet-400 uppercase tracking-widest">
                Tags
              </span>
              <div className="flex flex-wrap gap-1.5">
                {meta.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-700 px-2.5 py-0.5 text-[10px] font-medium text-violet-700 dark:text-violet-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Handle
        type="source"
        position={source}
        className="bg-violet-600! w-2.5! h-2.5! border-2! border-white! dark:border-zinc-950!"
      />
    </div>
  );
}
